import { useMemo } from 'react';

import { TagInput, type TagValidationResult } from '@/shared/ui/tag-input';
import { useToast } from '@/stores/pure/ToastStore';

type Mode = 'hashtag' | 'mention';

type StructuredTagInputProps = {
  mode: Mode;
  values: FilterKeyValue[];
  onChange: (values: FilterKeyValue[]) => void;
};

const HASHTAG_VALIDATION_REGEX = /^(?!\d+$)[\p{L}\p{N}\p{M}_ ]+$/u;
const MENTION_VALIDATION_REGEX = /^(?!\d+$)[\p{L}\p{N}\p{M}_. ]+$/u;

const getPrefix = (mode: Mode) => {
  return mode === 'hashtag' ? '#' : '@';
};

const stripPrefix = (value: string): string => {
  return value.replace(/^[@#]+/, '').trim();
};

const normalizeValue = (value: string, mode: Mode): string | null => {
  const cleaned = stripPrefix(value);
  if (!cleaned) return null;

  const valid =
    mode === 'hashtag'
      ? HASHTAG_VALIDATION_REGEX.test(cleaned)
      : MENTION_VALIDATION_REGEX.test(cleaned);

  if (!valid) return null;
  return cleaned;
};

const toDisplayValue = (value: string, mode: Mode): string => {
  return `${getPrefix(mode)}${stripPrefix(value)}`;
};

const toFilterValue = (value: string, mode: Mode): {
  filterKey: string;
  filterName: string;
} | null => {
  const normalized = normalizeValue(value, mode);
  if (!normalized) return null;
  return {
    filterKey: normalized,
    filterName: toDisplayValue(normalized, mode),
  };
};

const StructuredTagFilterInput = ({
  mode,
  values,
  onChange,
}: StructuredTagInputProps) => {
  const addToast = useToast();

  const displayValues = useMemo(
    () =>
      values
        .map((value) => toFilterValue(value.filterKey.toString(), mode))
        .filter((value): value is NonNullable<typeof value> => !!value)
        .map((value) => value.filterName),
    [mode, values],
  );

  const validateTag = (
    tag: string,
    currentTags: string[],
  ): TagValidationResult => {
    const normalized = normalizeValue(tag, mode);
    if (!normalized) return 'invalid';

    const hasDuplicate = currentTags
      .map((value) => stripPrefix(value))
      .includes(normalized);
    if (hasDuplicate) return 'duplicate';

    return null;
  };

  const transformOnAdd = (tag: string) => {
    const normalized = normalizeValue(tag, mode);
    if (!normalized) return null;
    return toDisplayValue(normalized, mode);
  };

  const onValidationError = (result: TagValidationResult) => {
    if (result === 'duplicate') {
      addToast(`This ${mode} has already been added`, 'fail');
      return;
    }

    if (result === 'invalid') {
      if (mode === 'hashtag') {
        addToast(
          'Hashtags can only contain letters, numbers, underscores. Numbers only are not allowed.',
          'fail',
        );
        return;
      }

      addToast(
        'Mentions can only contain letters, numbers, underscores, dots. Numbers only are not allowed.',
        'fail',
      );
    }
  };

  const handleChange = (nextDisplayValues: string[]) => {
    const nextFilterValues = nextDisplayValues
      .map((value) => toFilterValue(value, mode))
      .filter((value): value is NonNullable<typeof value> => !!value);

    onChange(nextFilterValues);
  };

  return (
    <div className="w-100 p-2">
      <TagInput
        value={displayValues}
        onChange={handleChange}
        placeholder={`Enter ${mode}`}
        validateTag={validateTag}
        transformOnAdd={transformOnAdd}
        onValidationError={onValidationError}
        inputFilter={() => true}
      />
    </div>
  );
};

export const StructuredHashtagFilterInput = (
  props: Omit<StructuredTagInputProps, 'mode'>,
) => {
  return <StructuredTagFilterInput mode="hashtag" {...props} />;
};

export const StructuredMentionFilterInput = (
  props: Omit<StructuredTagInputProps, 'mode'>,
) => {
  return <StructuredTagFilterInput mode="mention" {...props} />;
};
